import React, { useEffect, useState } from 'react';
import { Plus, Trash2, IndianRupee, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

const lineStatusBadge = (line) => {
  if (line.payment_status === 'paid') {
    const m = line.payment_method === 'online_link' ? 'Link'
      : line.payment_method === 'bundled' ? 'Bundled' : 'Offline';
    return { text: `Paid · ${m}`, cls: 'bg-green-100 text-green-700' };
  }
  if (line.payment_method === 'bundled') return { text: 'Bundled (with court)', cls: 'bg-blue-100 text-blue-700' };
  if (line.payment_method === 'online_link') return { text: 'Link sent', cls: 'bg-amber-100 text-amber-700' };
  return { text: 'Unpaid', cls: 'bg-gray-100 text-gray-600' };
};

/**
 * Rentals / add-ons on an existing booking. Court vs rental payment are
 * independent; only "bundled" couples to the court payment.
 */
export const RentalsSection = ({ booking: initialBooking, onChanged }) => {
  const bookingId = initialBooking?.id;
  const facilityId = initialBooking?.facility_id || initialBooking?.facilityId;
  const [booking, setBooking] = useState(initialBooking);
  const [catalogue, setCatalogue] = useState([]);
  const [itemId, setItemId] = useState('');
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const items = await api.getAddonCatalogue(facilityId, true);
        if (active) {
          setCatalogue(items || []);
          if ((items || []).length) setItemId(items[0].id);
        }
      } catch (e) { /* catalogue may be empty for non-padel venues */ }
      try {
        const fresh = await api.getBooking(bookingId);
        if (active && fresh) setBooking(fresh);
      } catch (e) { /* ignore */ }
    })();
    return () => { active = false; };
  }, [bookingId, facilityId]);

  const refresh = (updated) => {
    if (updated && updated.id) setBooking(updated);
    if (onChanged) onChanged(updated);
  };

  const handleAdd = async () => {
    if (!itemId) return;
    setBusy(true);
    try {
      const updated = await api.addBookingAddon(bookingId, itemId, parseInt(qty, 10) || 1);
      refresh(updated);
      setQty(1);
      toast({ title: 'Rental added', description: 'Now choose how it is paid.' });
    } catch (e) {
      toast({ title: 'Could not add rental', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const handleCollect = async (lineId, method) => {
    setBusy(true);
    try {
      const res = await api.collectBookingAddon(bookingId, lineId, method);
      // online_link returns { booking, short_url }; others return the booking
      if (res && res.short_url) {
        refresh(res.booking);
        toast({ title: 'Payment link sent', description: res.short_url });
      } else {
        refresh(res);
        toast({ title: method === 'bundled' ? 'Added to booking total' : 'Rental collected' });
      }
    } catch (e) {
      toast({ title: 'Payment failed', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const handleRemove = async (line) => {
    const hasMoney = line.payment_status === 'paid' || (line.amount_allocated || 0) > 0;
    if (hasMoney && !window.confirm('This rental has money collected against it. Removing it will NOT auto-refund — handle the refund manually. Remove anyway?')) return;
    setBusy(true);
    try {
      const updated = await api.removeBookingAddon(bookingId, line.id, hasMoney);
      refresh(updated);
      toast({ title: 'Rental removed' });
    } catch (e) {
      toast({ title: 'Could not remove', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const addons = booking?.addons || [];
  const courtAmount = booking?.amount || 0;
  const grandTotal = booking?.grand_total ?? (courtAmount + (booking?.addons_total || 0));
  const grandCollected = booking?.grand_collected ?? ((booking?.amount_paid || 0) + (booking?.addons_paid || 0));

  return (
    <div className="border-t pt-3 mt-3" data-testid="rentals-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-700">Add-ons / Rentals</h4>
        {addons.length > 0 && (
          <span className="text-xs text-gray-500" data-testid="rentals-grand-total">
            Total ₹{grandTotal} · Collected ₹{grandCollected}
          </span>
        )}
      </div>

      {addons.length === 0 && (
        <p className="text-xs text-gray-400 mb-2" data-testid="rentals-empty">No rentals added.</p>
      )}

      <div className="space-y-2">
        {addons.map((line) => {
          const badge = lineStatusBadge(line);
          const unpaid = line.payment_status !== 'paid';
          return (
            <div key={line.id} className="flex flex-wrap items-center gap-2 bg-gray-50 rounded-lg p-2" data-testid={`rental-line-${line.id}`}>
              <div className="flex-1 min-w-[140px]">
                <p className="text-sm font-medium text-gray-800">{line.name} × {line.quantity}</p>
                <p className="text-xs text-gray-500">₹{line.line_total}</p>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${badge.cls}`} data-testid={`rental-status-${line.id}`}>{badge.text}</span>
              {unpaid && (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy}
                    onClick={() => handleCollect(line.id, 'offline')} data-testid={`rental-offline-${line.id}`}>Collect offline</Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy}
                    onClick={() => handleCollect(line.id, 'bundled')} data-testid={`rental-bundled-${line.id}`}>Add to total</Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy}
                    onClick={() => handleCollect(line.id, 'online_link')} data-testid={`rental-link-${line.id}`}>Send link</Button>
                </div>
              )}
              <button type="button" onClick={() => handleRemove(line)} disabled={busy}
                className="text-gray-400 hover:text-red-500" data-testid={`rental-remove-${line.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      {catalogue.length > 0 ? (
        <div className="flex items-end gap-2 mt-3">
          <div className="flex-1">
            <label className="text-xs text-gray-500">Item</label>
            <select value={itemId} onChange={(e) => setItemId(e.target.value)}
              className="w-full h-9 border rounded-md px-2 text-sm" data-testid="rental-item-select">
              {catalogue.map((it) => (
                <option key={it.id} value={it.id}>{it.name} — ₹{it.unit_price}</option>
              ))}
            </select>
          </div>
          <div className="w-20">
            <label className="text-xs text-gray-500">Qty</label>
            <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)}
              className="h-9" data-testid="rental-qty-input" />
          </div>
          <Button size="sm" className="h-9" onClick={handleAdd} disabled={busy || !itemId} data-testid="rental-add-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Rental
          </Button>
        </div>
      ) : (
        <p className="text-xs text-gray-400 mt-2" data-testid="rentals-no-catalogue">
          No rental items configured for this venue. Add them in Settings → Rental Catalogue.
        </p>
      )}
    </div>
  );
};

export default RentalsSection;
